import styled from 'styled-components/macro'

export const PageWrapper = styled.div`
  width: 100%;
  padding: 1rem;
  display: flex;
  justify-content: center;
`

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`

export const TitleWrapper = styled.div`
  margin-bottom: 1rem;
`

export const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.backgroundSurface};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
`

export const TabButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: none;
  background-color: ${({ active, theme }) => (active ? theme.accentAction : 'transparent')};
  color: ${({ active, theme }) => (active ? theme.white : theme.textPrimary)};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active, theme }) => (active ? theme.accentAction : theme.backgroundModule)};
  }
`

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1rem;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const FunctionCardContainer = styled.div`
  background-color: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
  padding: 1.25rem;
`

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`

export const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  margin: 0;
`

export const CardDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  margin: 0 0 1rem 0;
  line-height: 1.4;
`

export const Badge = styled.span<{ variant?: 'read' | 'write' | 'network' }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background-color: ${({ theme, variant }) =>
    variant === 'read'
      ? theme.accentSuccess + '20'
      : variant === 'write'
      ? theme.accentWarning + '20'
      : variant === 'network'
      ? theme.accentAction + '20'
      : theme.backgroundOutline};
  color: ${({ theme, variant }) =>
    variant === 'read'
      ? theme.accentSuccess
      : variant === 'write'
      ? theme.accentWarning
      : variant === 'network'
      ? theme.accentAction
      : theme.textSecondary};
`

export const InputGroup = styled.div`
  margin-bottom: 1rem;
`

export const InputLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 0.5rem;
`

export const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.backgroundInteractive};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.accentAction};
  }

  &::placeholder {
    color: ${({ theme }) => theme.textSecondary};
  }
`

export const CurrentValueDisplay = styled.div`
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 8px;
  margin-bottom: 1rem;
`

export const CurrentValueLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`

export const CurrentValueText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  margin-left: 0.5rem;
`

export const ExecuteButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.875rem;
  border: none;
  border-radius: 12px;
  background-color: ${({ theme, disabled }) => (disabled ? theme.backgroundOutline : theme.accentAction)};
  color: ${({ theme, disabled }) => (disabled ? theme.textSecondary : theme.white)};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`

export const ResultDisplay = styled.div<{ error?: boolean }>`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: ${({ theme, error }) => (error ? theme.accentFailure + '10' : theme.backgroundModule)};
  border: 1px solid ${({ theme, error }) => (error ? theme.accentFailure : theme.backgroundOutline)};
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme, error }) => (error ? theme.accentFailure : theme.textPrimary)};
  word-break: break-all;
`

export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const NetworkIndicatorContainer = styled.div<{ network: 'mainnet' | 'testnet' | 'devnet' | 'unknown' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  background-color: ${({ theme, network }) =>
    network === 'mainnet'
      ? theme.accentSuccess + '15'
      : network === 'testnet'
      ? theme.accentWarning + '15'
      : network === 'devnet'
      ? theme.accentAction + '15'
      : theme.backgroundOutline};
  border: 1px solid
    ${({ theme, network }) =>
      network === 'mainnet'
        ? theme.accentSuccess + '30'
        : network === 'testnet'
        ? theme.accentWarning + '30'
        : network === 'devnet'
        ? theme.accentAction + '30'
        : theme.backgroundOutline};
`

export const NetworkDot = styled.div<{ network: 'mainnet' | 'testnet' | 'devnet' | 'unknown' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme, network }) =>
    network === 'mainnet'
      ? theme.accentSuccess
      : network === 'testnet'
      ? theme.accentWarning
      : network === 'devnet'
      ? theme.accentAction
      : theme.textSecondary};
`

export const NetworkText = styled.span<{ network: 'mainnet' | 'testnet' | 'devnet' | 'unknown' }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme, network }) =>
    network === 'mainnet'
      ? theme.accentSuccess
      : network === 'testnet'
      ? theme.accentWarning
      : network === 'devnet'
      ? theme.accentAction
      : theme.textSecondary};
`

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  margin: 0 0 1rem 0;
`

export const ConnectWalletMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
`
